// ** React Imports **
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';

// ** Components **
import AuthHeader from '../../components/authentication/AuthHeader';
import AuthIntroSection from '../../components/authentication/AuthIntroSection';
import RegisterStepAccount from './steps/RegisterStepAccount';
import RegisterStepBusinessInfo from './steps/RegisterStepBusinessInfo';
import RegisterStepLocation from './steps/RegisterStepLocation';
import RegisterStepImagesUpload from './steps/RegisterStepImagesUpload';
import RegisterStepReview from './steps/RegisterStepReview';

// ** Styles **
import './RegisterFlow.css';

/**
 * RegisterFlow Component
 * 
 * Multi-step registration and business onboarding flow
 * 
 * Flow:
 * Step 0: Account Creation (Google/Microsoft/Manual)
 * Step 1: Business Registration (business details)
 * Step 2: Business Location (map + address)
 * Step 3: Business Images Upload (profile + cover)
 * Step 4: Review Images (preview + final submit)
 * 
 * Features:
 * - Preserves all existing authentication logic
 * - Prevents redirect to home until all steps complete
 * - Maintains state across steps
 * - Shows progress indicator
 * 
 * @component
 */
const RegisterFlow = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  
  // ** State Management **
  // Current step in the flow (0-4)
  const [currentStep, setCurrentStep] = useState(0);
  
  // User data from account creation (Step 0)
  const [userData, setUserData] = useState(null);
  
  // Business form data (Step 1)
  const [businessData, setBusinessData] = useState({
    designation: '',
    name: '',
    businessCategory: '',
    email: '',
    mobile: '+91',
    vatNumber: '',
    taxRefNumber: '',
    registrationNumber: '',
  });
  
  // Location data (Step 2)
  const [locationData, setLocationData] = useState({
    businessLocationName: '',
    businessLocationUserName: '',
    physicalAddress: '',
    coordinates: null,
    currency: null,
    currencySymbol: null,
    media: [],
  });
  
  // Images data (Step 3 & 4)
  const [imagesData, setImagesData] = useState({
    profileImage: null,
    profileImagePreview: null,
    coverImage: null,
    coverImagePreview: null,
  });

  /**
   * Handle completion of Step 0 (Account Creation)
   * Called after successful signup or SSO
   * 
   * @param {Object} user - User data from registration/SSO
   */
  const handleAccountCreated = (user) => {
    setUserData(user);
    setCurrentStep(1); // Move to Business Registration
  };

  /**
   * Handle completion of Step 1 (Business Registration)
   * 
   * @param {Object} data - Business form data
   */
  const handleBusinessInfoComplete = (data) => {
    setBusinessData(data);
    setCurrentStep(2); // Move to Location
  };

  /**
   * Handle completion of Step 2 (Location)
   * 
   * @param {Object} data - Location data
   */
  const handleLocationComplete = (data) => {
    setLocationData(data);
    setCurrentStep(3); // Move to Images Upload
  };

  /**
   * Handle completion of Step 3 (Images Upload)
   * 
   * @param {Object} data - Images data
   */
  const handleImagesUploadComplete = (data) => {
    setImagesData(data);
    setCurrentStep(4); // Move to Review
  };

  /**
   * Handle completion of Step 4 (Review & Final Submit)
   * This completes the entire onboarding flow
   */
  const handleFinalSubmit = () => {
    // Redirect to home/dashboard after complete onboarding
    navigate('/home');
  };

  /**
   * Handle backward navigation via stepper
   * Allow users to go back to previous steps
   * 
   * @param {number} stepNumber - Step number to navigate to (1-3)
   */
  const handleStepClick = (stepNumber) => {
    // Convert display step (1-3) to internal step (0-4)
    // Step 1 = internal 0 or 1
    // Step 2 = internal 2
    // Step 3 = internal 3 or 4
    
    if (stepNumber === 1) {
      setCurrentStep(1); // Go to Business Info (skip Account Creation)
    } else if (stepNumber === 2) {
      setCurrentStep(2); // Go to Location
    } else if (stepNumber === 3) {
      setCurrentStep(3); // Go to Images Upload
    }
  };

  /**
   * Render the appropriate step component based on currentStep
   */
  const renderStep = () => {
    switch (currentStep) {
      case 0:
        console.log("RegisterStepAccount", currentStep);
        return (
          <RegisterStepAccount 
            onComplete={handleAccountCreated}
          />
        );
      
      case 1:
        console.log("RegisterStepBusinessInfo", currentStep);
        return (
          <RegisterStepBusinessInfo 
            userData={userData}
            initialData={businessData}
            onComplete={handleBusinessInfoComplete}
            onStepClick={handleStepClick}
          />
        );
      
      case 2:
        console.log("RegisterStepLocation", currentStep);
        return (
          <RegisterStepLocation 
            userData={userData}
            businessData={businessData}
            initialData={locationData}
            onComplete={handleLocationComplete}
            onStepClick={handleStepClick}
          />
        );
      
      case 3:
        return (
          <RegisterStepImagesUpload 
            userData={userData}
            businessData={businessData}
            locationData={locationData}
            initialData={imagesData}
            onComplete={handleImagesUploadComplete}
            onStepClick={handleStepClick}
          />
        );
      
      case 4:
        return (
          <RegisterStepReview 
            userData={userData}
            businessData={businessData}
            locationData={locationData}
            imagesData={imagesData}
            onComplete={handleFinalSubmit}
            onStepClick={handleStepClick}
            onDeleteImage={setImagesData}
          />
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="register-flow">
      {/* Header */}
      <AuthHeader />

      {/* Main Content - Two Column Layout */}
      <div className="register-flow__content">
        {/* Left Section - Branding & Illustration (constant across all steps) */}
        <AuthIntroSection
          heading={t('introHeading')}
          description={t('introDescription')}
          image={null}
          imageAlt={t('welcomeIllustration')}
        />

        {/* Right Section - Step Content */}
        <div className="register-flow__card-wrapper">
          {renderStep()}
        </div>
      </div>
    </div>
  );
};

export default RegisterFlow;
